import { Button, message } from 'antd'
import { DrilldownDataContext } from 'features/drilldown'
import {
  DRILLDOWN_PANEL_CONFIGS,
  DRILLDOWN_PANEL_INITIAL_DATA,
} from 'features/drilldown/components/panelConfigs/PanelConfigs'
import { useDrilldownPanelCreation } from 'features/drilldown/data/DrilldownPanelCreationContext'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import { useExplorerMutation } from 'features/drilldown/data/savedExplorationState/useExplorerMutation'
import {
  FIELD_TYPE_OPERATORS,
  OPERATOR_NAMES,
} from 'features/drilldown/panels/global/filters/DrilldownFilterRow'
import { OntologyContextType, useOntology } from 'features/dynamicOntology'
import { useGeckoNotifications } from 'features/notifications'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { v4 } from 'uuid'

import { ChatInput, MessagesArea, SessionSidebar } from './components'
import { ChatMessageResponse, useChat } from './gongChatApi'
import { sendExplorerChatTurn } from './sendExplorerChatTurn'
import { ChatMessage, LlmPanelData } from './types'
import { useChatSessions } from './useChatSessions'
import {
  buildAssistantContent,
  buildHistoricalAssistantContent,
  selectAssistantAction,
  stripMarkdown,
  summarizePanelRequests,
} from './utils'

const PanelContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: min(70vh, 40rem);
  width: 100%;
  min-height: 0;
`

const ChatColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
`

const UndoActionContainer = styled.div`
  padding: 0 16px 8px;
`

/**
 * Frontend-owned reference context (ontology + panel catalog + schemas) sent to
 * the chat backend as `client_context`. This block supplies the panel
 * schema/guidance used by either the legacy `create_panels` tool or the
 * snapshot-aware `mutate_dashboard` tool.
 */
const buildClientContext = (ontology: OntologyContextType) => `
    -------------
    Context
    -------------
    You are helping a user of an app that can filter and display data. The types in the schema are:
    ${JSON.stringify(ontology.entryTypeNames)}

    In addition, you know the following fields on those types:
    ${JSON.stringify(ontology.fields)}


    Each panel applies any filters from previous panels.
    The panels that can be made are:
    ${JSON.stringify(DRILLDOWN_PANEL_CONFIGS)}

    The panels' data schemas may and often should be edited when building panels for users. These are:
    ${JSON.stringify(DRILLDOWN_PANEL_INITIAL_DATA)}

    Note the filter panel schema info here:
    '''
    export type Filter = {
        id: string
        state: FilterState
    }
    export interface FilterState {
        field: DrilldownField
        operator: RolodexHistogramFilterOp
        value?: FilterValue
    }
    ${JSON.stringify(OPERATOR_NAMES)}
    ${JSON.stringify(FIELD_TYPE_OPERATORS)}
    Note that the 'in' operation requires its value to be an array of strings.
    Note that the date filter uses the format YYYY-MM-DD like 2025-06-19.
    '''

    -------------
    Filtering by a related entity
    -------------
    * When a filter value names a specific entity (a name/label), it is usually a property of that entity's type, not of the type being counted. Set the filter's \`type\` to the entity that owns the property and include that entity in the query \`links\`. Do not put one entity's property onto the type being counted.
    * Example: to count \`<ChildType>\` scoped to a \`<ParentType>\` named \`<name>\`, the query root \`type\` is \`<ChildType>\`, \`links\` includes \`<ParentType>\`, and the filter is \`{ "type": "<ParentType>", "key": "name", "op": "~", "value": "<name>" }\` — not \`{ "type": "<ChildType>", "key": "name", ... }\`.
    * Prefer \`~\` (Contains) over \`=\` (Equals) for name/text matches, unless the user explicitly asks for an exact match.
    -------------
    Panel Creation Guidance
    -------------
    When the user asks you to build panels, use the panel request shape
    { "panelType": ..., "initialDataOverride": ... }. Put it in the tool format
    specified by the system instructions: \`create_panels\` uses an array of panel requests,
    while \`mutate_dashboard\` uses a \`create\` operation whose \`panel\` is a panel request.
    * Follow the schema for panel state closely. If a value is non-nullable and can't be undefined, then don't make it null or undefined.
    * A \`columnBreak\` panel is a layout marker. Create it immediately before the first panel in a new column, and remove it when the user asks to merge columns.
    * Try to give each panel a title that is a good summary of the data it will display.
    * Use the schemas in the context to help with filling in panel state, especially for filters and histograms.
    * Don't make a column break panel unless the user asked for a new column in the current message.
    * Note that inspection types tend to be lowercase in the data ('raut', 'rug', etc).
    * If you want to apply multiple consecutive filters, prefer putting them all into a single panel rather than multiple panels. But only if there are no other panels between them.
    * If a user wants to have a discussion or ask about the ontology or panel schemas, feel free to chat to them and help them out instead of making panels.
    * Count the entity the user names: match the noun in their request to a type's name in the schema above, and set both the query root \`type\` and the counting \`select\` entry to that type. Do not assume a fixed default entity.
    * Only add a grouping/breakdown (a \`group_by\` on a different type) when the user explicitly asks for one (phrasing like "by X", "per X", "broken down by X"). Otherwise group/count on the entity being counted to produce a single total.
    * When the entity to count is ambiguous, state which entity you are counting in the reply so a wrong guess is visible.
    * When building a Counts (\`counts\`) panel, keep the panel's default \`activeCountTypes\` (as provided in the panel initial-data context) and scope it by adding a filter panel. Only remove/limit tiles when the user explicitly asks for a subset. Prefer additive scoping (add a filter) over subtractive (dropping metrics).
`
const mapServerMessages = (
  history: ReadonlyArray<ChatMessageResponse>
): ChatMessage[] =>
  history.map((m) => ({
    id: m.id,
    role: m.role,
    content:
      m.role === 'assistant'
        ? buildHistoricalAssistantContent(m.content, m.actions)
        : m.content,
    timestamp: new Date(m.created_at).toLocaleTimeString(),
  }))

/**
 * Saved dashboards can contain LLM panel data created before messages were
 * persisted as an array. Treat an invalid value as an empty history so a
 * follow-up chat message can repair the panel instead of crashing while
 * spreading the previous state.
 */
const getMessages = (value: unknown): ReadonlyArray<ChatMessage> =>
  Array.isArray(value) ? (value as ReadonlyArray<ChatMessage>) : []

const RawLlmPanel: FC = () => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<LlmPanelData>()
  const ontology = useOntology()
  const { orgId, siteId } = useContext(DrilldownDataContext)
  const {
    applyMutation,
    buildCurrentSnapshot,
    undoLastMutation,
    canUndoLastMutation,
  } = useExplorerMutation()
  const { createPanels } = useDrilldownPanelCreation()
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { ensureSession, loadMessages, sendMessage } = useChat()
  const {
    sessions,
    isLoading: sessionsLoading,
    refresh,
    remove,
  } = useChatSessions(orgId)

  const [isLoading, setIsLoading] = useState(false)
  const [isHydrating, setIsHydrating] = useState(false)

  const messages = getMessages(panelData.messages)
  const sessionId = panelData.sessionId
  const sidebarCollapsed = panelData.settings.sidebarCollapsed ?? false

  const setMessages: Dispatch<SetStateAction<ReadonlyArray<ChatMessage>>> =
    useCallback(
      (val) =>
        setPanelData((prev) => {
          const previousMessages = getMessages(prev.messages)
          const newVal = typeof val === 'function' ? val(previousMessages) : val
          return {
            ...prev,
            messages: newVal,
          }
        }),
      [setPanelData]
    )

  const resetToNewSession = useCallback(() => {
    setPanelData((prev) => ({
      ...prev,
      sessionId: undefined,
      messages: [],
    }))
  }, [setPanelData])

  const clientContext = useMemo(() => buildClientContext(ontology), [ontology])

  const addMessage = useCallback(
    (content: string, role: 'user' | 'assistant') => {
      const newMessage: ChatMessage = {
        id: v4(),
        content,
        role,
        timestamp: new Date().toLocaleTimeString(),
      }
      setMessages((prev) => [...prev, newMessage])
    },
    [setMessages]
  )

  const handleSelectSession = useCallback(
    (id: string) => {
      setPanelData((prev) => ({
        ...prev,
        sessionId: id,
        messages: [],
      }))
    },
    [setPanelData]
  )

  const handleNewSession = useCallback(() => {
    resetToNewSession()
  }, [resetToNewSession])

  const handleDeleteSession = useCallback(
    async (id: string) => {
      const wasActive = id === sessionId
      try {
        await remove(id)
        if (wasActive) {
          resetToNewSession()
        }
      } catch (error) {
        console.error('Failed to delete chat session', error)
        errorNotification('Failed to delete conversation. Please try again.')
      }
    },
    [sessionId, remove, resetToNewSession, errorNotification]
  )

  const handleToggleSidebar = useCallback(() => {
    setPanelData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        sidebarCollapsed: !(prev.settings.sidebarCollapsed ?? false),
      },
    }))
  }, [setPanelData])

  // Hydrate server-side history when the user selects a past session from the
  // sidebar. Past panel-creation turns are shown as static summaries only - we
  // do NOT re-materialize panels onto the canvas.
  const hydratedSessionIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!sessionId) {
      hydratedSessionIdRef.current = undefined
      return
    }
    if (hydratedSessionIdRef.current === sessionId) return
    if (messages.length > 0) {
      hydratedSessionIdRef.current = sessionId
      return
    }

    let cancelled = false
    setIsHydrating(true)
    loadMessages(sessionId)
      .then((history) => {
        if (cancelled) return
        hydratedSessionIdRef.current = sessionId
        if (history.length === 0) return
        setMessages(mapServerMessages(history))
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to load chat history', error)
      })
      .finally(() => {
        if (cancelled) return
        setIsHydrating(false)
      })

    return () => {
      cancelled = true
    }
  }, [sessionId, messages.length, loadMessages, setMessages])

  const handleSendMessage = useCallback(
    async (rawText: string, unitIds: string[]) => {
      const text = rawText.trim()
      if (!text || isLoading) return
      addMessage(text, 'user')
      setIsLoading(true)
      try {
        if (!orgId) {
          throw new Error('No organization is selected for this exploration.')
        }
        const requestId = v4()
        const { response } = await sendExplorerChatTurn({
          orgId,
          sessionId: panelData.sessionId,
          content: text,
          unitIds,
          clientContext,
          requestId,
          buildCurrentSnapshot,
          ensureSession,
          sendMessage,
          onSessionCreated: (createdSessionId) => {
            setPanelData((prev) => ({
              ...prev,
              sessionId: createdSessionId,
            }))
            hydratedSessionIdRef.current = createdSessionId
          },
        })
        const selectedAction = selectAssistantAction(response.actions)
        let actionSummary: string | undefined
        if (selectedAction.kind === 'mutation') {
          const applied = applyMutation(selectedAction.payload, requestId)
          if (!applied.ok) {
            errorNotification(applied.reason)
          } else {
            actionSummary = applied.summary
            successNotification('Explorer updated via AI.')
          }
        } else if (selectedAction.kind === 'legacy-panels') {
          createPanels(selectedAction.panels)
          actionSummary = summarizePanelRequests(selectedAction.panels)
          successNotification(
            selectedAction.panels.length > 1
              ? 'Panels created via AI.'
              : 'Panel created via AI.'
          )
        }
        addMessage(
          buildAssistantContent(response.content, actionSummary),
          'assistant'
        )
        await refresh()
      } catch {
        errorNotification(
          'Encountered an error reaching the AI. Please try again later.'
        )
        addMessage(
          'Sorry, I encountered an error. Please try again.',
          'assistant'
        )
      } finally {
        setIsLoading(false)
      }
    },
    [
      isLoading,
      addMessage,
      orgId,
      panelData.sessionId,
      ensureSession,
      setPanelData,
      sendMessage,
      clientContext,
      buildCurrentSnapshot,
      applyMutation,
      createPanels,
      successNotification,
      errorNotification,
      refresh,
    ]
  )

  // TODO (xdowns) migrate to Explorer shared copy function
  const handleCopy = useCallback((content: string) => {
    const plainText = stripMarkdown(content)
    navigator.clipboard.writeText(plainText)
    message.success('Response copied to clipboard!')
  }, [])

  return (
    <PanelContainer>
      <SessionSidebar
        sessions={sessions}
        activeSessionId={sessionId}
        isLoading={sessionsLoading}
        collapsed={sidebarCollapsed}
        onSelect={handleSelectSession}
        onNew={handleNewSession}
        onDelete={handleDeleteSession}
        onToggleCollapse={handleToggleSidebar}
      />

      <ChatColumn>
        <MessagesArea
          messages={messages}
          showTimestamps={panelData.settings.showTimestamps}
          isLoading={isLoading}
          isHydrating={isHydrating}
          onCopy={handleCopy}
        />

        {canUndoLastMutation && (
          <UndoActionContainer>
            <Button
              type='link'
              onClick={() => {
                if (undoLastMutation()) {
                  successNotification('Reverted the last AI dashboard change.')
                }
              }}>
              Undo dashboard change
            </Button>
          </UndoActionContainer>
        )}

        <ChatInput
          onSend={handleSendMessage}
          isLoading={isLoading}
          orgId={orgId}
          siteId={siteId}
        />
      </ChatColumn>
    </PanelContainer>
  )
}

export const LlmPanel = React.memo(RawLlmPanel)
